import { useCallback } from 'react';
import { analytics } from '@/services/analytics';
import { useAuth } from '@/contexts/AuthContext';

export const useAnalytics = () => {
  const { user } = useAuth();

  // Track any custom event
  const track = useCallback((eventName: string, properties?: Record<string, any>) => {
    analytics.track(eventName, properties);
  }, []);

  // Update user properties (e.g., when profile changes)
  const updateUserProperties = useCallback(async () => {
    if (user) {
      await analytics.updateUserProperties(user);
    }
  }, [user]);

  // 🎯 META-EQUIVALENT EVENTS (NEW)
  
  // Install (equivalent to Meta's automatic Install)
  const trackInstall = useCallback(async () => {
    await analytics.trackInstall();
  }, []);

  // Sign Up (equivalent to Meta's CompleteRegistration)
  const trackSignUp = useCallback((signupMethod: string = 'google', firstName?: string, lastName?: string) => {
    track('Sign Up', {
      email: user?.email,
      first_name: firstName,
      last_name: lastName,
      signup_method: signupMethod,
      source: 'web' // TODO: detect platform
    });
  }, [track, user]);

  // Chapter Viewed (equivalent to Meta's ViewContent)
  const trackChapterViewed = useCallback((storyId: string, chapterId: string, storyTitle: string, chapterTitle?: string) => {
    track('Chapter Viewed', {
      story_id: storyId,
      chapter_id: chapterId,
      story_title: storyTitle,
      chapter_title: chapterTitle,
      content_type: 'chapter',
      view_duration: 5 // Meta tracks after 5 seconds
    });
  }, [track]);

  // Checkout Initiated (equivalent to Meta's InitiateCheckout)
  const trackCheckoutInitiated = useCallback((planName: string, planPrice: number, billingPeriod: string, currency: string = 'BRL') => {
    track('Checkout Initiated', {
      plan_name: planName,
      plan_price: planPrice,
      billing_period: billingPeriod,
      currency: currency,
      content_category: 'subscription'
    });
  }, [track]);

  // Purchase Completed (equivalent to Meta's Purchase)
  const trackPurchaseCompleted = useCallback((amount: number, planName: string, subscriptionId?: string, currency: string = 'BRL') => {
    track('Purchase Completed', {
      amount: amount,
      plan_name: planName,
      subscription_id: subscriptionId,
      currency: currency,
      content_category: 'subscription'
    });
  }, [track]);

  // 🎯 EXISTING FUNCTIONS (keeping for backward compatibility)
  const trackChapterRead = useCallback((storyId: string, chapterId: string, percentRead: number) => {
    track('Chapter Read', {
      story_id: storyId,
      chapter_id: chapterId,
      percent_read: percentRead,
      read_completed: percentRead >= 85
    });
  }, [track]);

  const trackStoryStarted = useCallback((storyId: string, storyTitle: string) => {
    track('Story Started', {
      story_id: storyId,
      story_title: storyTitle
    });
  }, [track]);

  const trackPurchase = useCallback((amount: number, coins: number, method: string) => {
    track('Purchase Made', {
      amount,
      coins,
      payment_method: method
    });
  }, [track]);

  const trackSubscription = useCallback((planName: string, planPrice: number, interval: string) => {
    track('Subscription Started', {
      plan_name: planName,
      plan_price: planPrice,
      plan_interval: interval
    });
  }, [track]);

  const trackProfileUpdated = useCallback((fields: string[]) => {
    track('Profile Updated', {
      fields_updated: fields
    });
    updateUserProperties(); // Refresh user properties in Mixpanel
  }, [track, updateUserProperties]);

  return {
    // Core functions
    track,
    updateUserProperties,
    
    // Meta-equivalent events (NEW)
    trackInstall,
    trackSignUp,
    trackChapterViewed,
    trackCheckoutInitiated,
    trackPurchaseCompleted,
    
    // Existing functions
    trackChapterRead,
    trackStoryStarted,
    trackPurchase,
    trackSubscription,
    trackProfileUpdated
  };
}; 